import React, { useEffect } from "react";
import { Box, Typography, Stack, useTheme } from "@mui/material";
import LockOutlined from "@mui/icons-material/LockOutlined";
import BlockOutlined from "@mui/icons-material/BlockOutlined";
import ButtonComponent from "../../components/Button.Component";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../services/utils";
import i18next from "i18next";
import { getConfig } from "../../config/runtimeConfig";

interface Forbidden403Props {
  title?: string;
  message?: string;
  primaryActionLabel?: string; // e.g., Return to Home
  secondaryActionLabel?: string; // e.g., Go Back
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
}

/**
 * 403 Forbidden Page (UI only)
 * - Matches existing MUI theme and button styling (btn-orange / btn-border)
 * - No routing or permission logic implemented here
 */
const looseT = i18next.t as any as (
  key: string,
  options?: Record<string, any>
) => string;

const Forbidden403: React.FC<Forbidden403Props> = ({
  title = looseT("auth.forbidden403.title"),
  message = looseT("auth.forbidden403.message"),
  primaryActionLabel = looseT("auth.forbidden403.primaryAction"),
  secondaryActionLabel = looseT("auth.forbidden403.secondaryAction"),
  onPrimaryAction,
  onSecondaryAction,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showToast } = useToast();
  // Show toast message if there's a stored 403 error message
  useEffect(() => {
    try {
      const errorMessage = sessionStorage.getItem("403ErrorMessage");
      if (errorMessage) {
        // Show the toast message on the 403 page
        showToast(errorMessage, "error");
        // Clear the message after displaying it to prevent stale data
        sessionStorage.removeItem("403ErrorMessage");
      }
    } catch (e) {
      console.warn(
        "Could not retrieve 403 error message from sessionStorage:",
        e
      );
    }
  }, [showToast]);

  // Handlers: use provided callbacks if passed, else perform defaults
  const handleSecondary = () => {
    if (onSecondaryAction) return onSecondaryAction();
    // Default: Return to Home one step in history
    if (window.history.length > 1) navigate(-2);
    else navigate("/domain", { replace: true });
  };

  const handlePrimary = () => {
    if (onPrimaryAction) return onPrimaryAction();
    // Default: Return to Home
    navigate(`/${getConfig("REACT_APP_DEFAULT_ROUTE", "domain")}`);
  };

  return (
    <Box
      role="region"
      aria-label={looseT("auth.forbidden403.ariaLabel")}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        px: 2,
      }}
    >
      <Box
        className="text-box-card-white full-radius"
        sx={{
          width: "100%",
          maxWidth: 720,
          p: { xs: 3, sm: 4 },
          borderColor: "divider",
          textAlign: "center",
          backgroundColor:
            theme.palette.mode === "light"
              ? "background.default"
              : "background.alt",
        }}
      >
        {/* Illustration */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              position: "relative",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 96,
              height: 96,
              borderRadius: "50%",
              backgroundColor:
                theme.palette.mode === "light"
                  ? theme.palette.secondary.light
                  : theme.palette.primary.main,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <LockOutlined
              sx={{ fontSize: 44, color: "primary.main", opacity: 0.9 }}
            />
            <Box
              sx={{
                position: "absolute",
                right: -6,
                bottom: -6,
                backgroundColor: theme.palette.background.paper,
                borderRadius: "50%",
                border: `1px solid ${theme.palette.divider}`,
                width: 34,
                height: 34,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: 0,
              }}
            >
              <BlockOutlined sx={{ fontSize: 20, color: "warning.main" }} />
            </Box>
          </Box>
        </Box>

        {/* Heading */}
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, mb: 1.5, color: "text.primary" }}
        >
          {title}
        </Typography>

        {/* Subtext */}
        <Typography
          variant="body1"
          sx={{ color: "text.secondary", maxWidth: 560, mx: "auto", mb: 3 }}
        >
          {message}
        </Typography>

        {/* Actions */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="center"
        >
          {secondaryActionLabel && (
            <ButtonComponent
              className="btn-orange btn-border"
              handelClickEvent={handleSecondary}
            >
              {secondaryActionLabel}
            </ButtonComponent>
          )}
          <ButtonComponent
            className="btn-orange"
            handelClickEvent={handlePrimary}
          >
            {primaryActionLabel}
          </ButtonComponent>
        </Stack>
      </Box>
    </Box>
  );
};

export default Forbidden403;
